import type {PDFDocumentProxy, RenderTask} from 'pdfjs-dist';
import {Alert, Button, DataStatus, useTranslationContext} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {useRef, useState} from 'react';

import {isNil} from 'ramda';

import {useOnMount} from 'shared/web';

const PAGE_SCALE = 1.5;
const MAX_PIXEL_RATIO = 2;
const PdfCanvas = styled.canvas`
  display: block;
  width: 100%;
  height: auto;
  &[hidden] {
    display: none;
  }
`;

type PdfPreviewPageRenderProps = {
  document: PDFDocumentProxy;
  pageNumber: number;
  onRetry: () => void;
};

export function PdfPreviewPageRender(props: PdfPreviewPageRenderProps) {
  const t = useTranslationContext();
  const canvas = useRef<HTMLCanvasElement>(null);
  const renderTask = useRef<RenderTask | null>(null);
  const activeController = useRef<AbortController | null>(null);
  const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading');

  useOnMount(
    () => {
      const controller = new AbortController();
      activeController.current = controller;
      void props.document
        .getPage(props.pageNumber)
        .then((page) => {
          controller.signal.throwIfAborted();
          const element = canvas.current;
          const context = element?.getContext('2d');
          if (isNil(element) || isNil(context)) {
            throw new Error('PDF canvas is unavailable');
          }
          const viewport = page.getViewport({scale: PAGE_SCALE});
          const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
          element.width = Math.ceil(viewport.width * pixelRatio);
          element.height = Math.ceil(viewport.height * pixelRatio);
          const task = page.render({
            canvas: element,
            canvasContext: context,
            viewport,
            transform: [pixelRatio, 0, 0, pixelRatio, 0, 0],
          });
          renderTask.current = task;
          return task.promise.then(() => {
            if (!controller.signal.aborted) {
              setStatus('ready');
            }
          });
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setStatus('error');
          }
        });
    },
    () => {
      activeController.current?.abort();
      renderTask.current?.cancel();
    }
  );

  return (
    <Box height="100%" overflow="auto">
      <VStack spacing={2}>
        <Show when={status === 'loading'}>
          <DataStatus isLoading minHeight={50} />
        </Show>
        <Show when={status === 'error'}>
          <Box padding={3}>
            <VStack spacing={3}>
              <Alert
                variant="error"
                type="inline"
                message={t('general.labels.somethingWentWrong')}
              />
              <HStack>
                <Button title={t('general.actions.retry')} onClick={props.onRetry} />
              </HStack>
            </VStack>
          </Box>
        </Show>
        <PdfCanvas
          ref={canvas}
          role="img"
          aria-label={`${t('general.actions.preview')} ${props.pageNumber}`}
          hidden={status !== 'ready'}
        />
      </VStack>
    </Box>
  );
}
